import { type ModelConfig } from '@meridian-ssa/shared';
import { useState } from 'react';

// Re-export for consumers
export type { ModelConfig };

type EnabledFilter = 'all' | 'enabled' | 'disabled';

interface ConfigSelectorProps {
  configs: ModelConfig[];
  selectedConfigIds: string[];
  onSelectionChange: (configIds: string[]) => void;
  defaultFilter?: EnabledFilter;
  showFilterButtons?: boolean;
}

export function ConfigSelector({
  configs,
  selectedConfigIds,
  onSelectionChange,
  defaultFilter = 'enabled',
  showFilterButtons = true,
}: ConfigSelectorProps) {
  const [enabledFilter, setEnabledFilter] =
    useState<EnabledFilter>(defaultFilter);

  // Filter configs based on enabled status
  const filteredConfigs = configs.filter(config => {
    if (enabledFilter === 'enabled') {
      return config.enabled !== false;
    } else if (enabledFilter === 'disabled') {
      return config.enabled === false;
    }
    return true; // "all"
  });

  function toggleConfig(id: string) {
    const newSelection = selectedConfigIds.includes(id)
      ? selectedConfigIds.filter(c => c !== id)
      : [...selectedConfigIds, id];
    onSelectionChange(newSelection);
  }

  function selectAll() {
    onSelectionChange(filteredConfigs.map(c => c.id));
  }

  function clearAll() {
    onSelectionChange([]);
  }

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-2">
        <label className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>
          Select Model Configs
        </label>
        <div className="flex gap-1.5">
          <button
            onClick={selectAll}
            className="px-2 py-0.5 rounded text-xs font-medium cursor-pointer"
            style={{ background: '#E8E0D8', color: '#1A1A1A', border: 'none' }}
          >
            Select All
          </button>
          <button
            onClick={clearAll}
            className="px-2 py-0.5 rounded text-xs font-medium cursor-pointer"
            style={{ background: '#E8E0D8', color: '#1A1A1A', border: 'none' }}
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Filter Buttons */}
      {showFilterButtons && (
        <div className="flex gap-1.5 mb-2">
          <button
            onClick={() => setEnabledFilter('enabled')}
            className="px-2 py-1 rounded text-xs font-medium cursor-pointer transition-all"
            style={{
              background: enabledFilter === 'enabled' ? '#8B2332' : 'white',
              color: enabledFilter === 'enabled' ? 'white' : '#1A1A1A',
              border: '1px solid #E8E0D8',
            }}
          >
            Enabled ({configs.filter(c => c.enabled !== false).length})
          </button>
          <button
            onClick={() => setEnabledFilter('disabled')}
            className="px-2 py-1 rounded text-xs font-medium cursor-pointer transition-all"
            style={{
              background: enabledFilter === 'disabled' ? '#8B2332' : 'white',
              color: enabledFilter === 'disabled' ? 'white' : '#1A1A1A',
              border: '1px solid #E8E0D8',
            }}
          >
            Disabled ({configs.filter(c => c.enabled === false).length})
          </button>
          <button
            onClick={() => setEnabledFilter('all')}
            className="px-2 py-1 rounded text-xs font-medium cursor-pointer transition-all"
            style={{
              background: enabledFilter === 'all' ? '#8B2332' : 'white',
              color: enabledFilter === 'all' ? 'white' : '#1A1A1A',
              border: '1px solid #E8E0D8',
            }}
          >
            All ({configs.length})
          </button>
        </div>
      )}

      {filteredConfigs.length === 0 ? (
        <p className="text-xs" style={{ color: '#A0A0A0' }}>
          {configs.length === 0
            ? 'No model configs available. Please create some configs first.'
            : `No ${enabledFilter} model configs available.`}
        </p>
      ) : (
        <div className="grid gap-1.5 max-h-56 overflow-y-auto pr-1">
          {filteredConfigs.map(config => (
            <label
              key={config.id}
              className="flex items-center px-2.5 py-2 rounded cursor-pointer transition-all"
              style={{
                border: `1.5px solid ${selectedConfigIds.includes(config.id) ? '#8B2332' : '#E8E0D8'}`,
                background: selectedConfigIds.includes(config.id)
                  ? 'rgba(139, 35, 50, 0.05)'
                  : 'white',
              }}
            >
              <input
                type="checkbox"
                checked={selectedConfigIds.includes(config.id)}
                onChange={() => toggleConfig(config.id)}
                className="mr-2 w-3.5 h-3.5"
                style={{ accentColor: '#8B2332' }}
              />
              <div className="flex-1">
                <div className="flex items-center gap-1.5">
                  <div
                    className="text-sm font-medium"
                    style={{ color: '#1A1A1A' }}
                  >
                    {config.name}
                  </div>
                  {config.enabled === false && (
                    <span
                      className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase"
                      style={{ background: '#B8860B', color: 'white' }}
                    >
                      Disabled
                    </span>
                  )}
                </div>
                <div className="text-xs" style={{ color: '#A0A0A0' }}>
                  {config.provider} • {config.api_model}
                </div>
              </div>
            </label>
          ))}
        </div>
      )}

      <div className="text-xs mt-2" style={{ color: '#A0A0A0' }}>
        {selectedConfigIds.length} config
        {selectedConfigIds.length !== 1 ? 's' : ''} selected
      </div>
    </div>
  );
}
